import { ChangeEventHandler, MouseEventHandler, useState } from "react";

import { useCreateNoShowReasonMutation } from "@domains/event/network/eventMutations";
import { GA_EVENT_IDS } from "@domains/common/constants/googleAnalyticsIds";
import useModalState from "@logics/hooks/useModalState";
import { ToastError } from "@logics/utils/toast";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import { EventInfoTrackingDataType } from "@domains/event/types/event";
import ReasonOptionSelect from "@domains/event/views/components/ReasonOptionSelect/ReasonOptionSelect";

import * as S from "./NoShowReasonBottomSheet.style";

const NO_SHOW_REASON = {
  FORGOT_EVENT: "Forgot About the Event",
  NOTIFICATION_ISSUE: "Didn't Receive Notification",
  ATTENDANCE_NOT_CHECKED: "Attended but Not Checked In by Host",
};

interface NoShowReasonBottomSheetProps {
  noShowEventId: number;
  eventInfo: EventInfoTrackingDataType;
  onSubmitReason: () => void;
}

const NoShowReasonBottomSheet = ({
  noShowEventId,
  eventInfo,
  onSubmitReason,
}: NoShowReasonBottomSheetProps) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [reasonContent, setReasonContent] = useState("");

  const { closeModal } = useModalState();

  const noShowReasonMutation = useCreateNoShowReasonMutation();

  const handleSelectReason = (reasonOption: string) => {
    setSelectedReason(reasonOption);
    setReasonContent("");
  };

  const handleChangeTextArea: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setReasonContent(e.target.value);
  };

  const handleSubmitReason: MouseEventHandler<HTMLButtonElement> = async (
    e,
  ) => {
    e.preventDefault();

    if (!selectedReason || noShowReasonMutation.isPending) return;

    try {
      await noShowReasonMutation.mutateAsync({
        eventId: String(noShowEventId),
        content: reasonContent.trim() || selectedReason, // NOTE: etc가 선택된 경우 textarea에 내용이 없으면 선택된 이유로 보냄
      });

      onSubmitReason();

      closeModal();
    } catch (error) {
      ToastError(ERROR_MESSAGE.SERVER_ERROR);
    }
  };

  return (
    <S.BottomSheetContainer>
      <S.HeaderWrapper>
        <S.Title name="m">
          Before Joining the {eventInfo.eventTitle} Event
        </S.Title>
        <S.CancelButton type="button" onClick={closeModal}>
          Cancel
        </S.CancelButton>
      </S.HeaderWrapper>
      <S.ScrollWrapper>
        <S.TextareaWrapper>
          <S.TextareaDescription name="strongS">
            {
              "It seems you couldn't attend a previous event.\nPlease let us know the reason for your absence.\nthis will greatly help us improve future events."
            }
          </S.TextareaDescription>

          <ReasonOptionSelect
            selectedOption={selectedReason}
            checkboxObject={NO_SHOW_REASON}
            etcTextarea={{
              labelText: "Other Reasons",
              labelKey: "etc",
              placeholder:
                "Please provide additional details about your reason for not attending.",
              value: reasonContent,
              onChange: handleChangeTextArea,
            }}
            onSelectOption={handleSelectReason}
          />
        </S.TextareaWrapper>
        <S.SubmitButton
          type="button"
          id={GA_EVENT_IDS.EVENT.GUEST_JOIN}
          disabled={
            !selectedReason ||
            (selectedReason === "etc" && !reasonContent.trim()) // NOTE: reason이 선택되지 않았거나, etc가 선택된 경우 textarea에 내용이 없으면 비활성화
          }
          onClick={handleSubmitReason}
        >
          Submit & Join
        </S.SubmitButton>
      </S.ScrollWrapper>
    </S.BottomSheetContainer>
  );
};

export default NoShowReasonBottomSheet;
